<template>
    <div class="ocr-form">
        <div class="ocr-form__header">
            <Heading
                :labelTitle="$t.__('ocr.OcrVerificationForm.verification_space')"
                showBackButton
                @go-back="cancel"
            />
            <div class="ocr-form__header__info">
                <span class="typographies-paragraph-medium-semibold">{{ documentTypeLabel }}</span>
                <template v-if="multipleVerify">
                    <span class="ocr-form__header-remaining"> | </span>
                    <!-- eslint-disable vue/no-v-html -->
                    <span
                        class="ocr-form__header-remaining"
                        v-html="
                            $t.__('ocr.OcrVerificationForm.label_remaining', {
                                current: completeRows + 1,
                                total: totalRows,
                            })
                        "
                    />
                    <!-- eslint-enable vue/no-v-html -->
                </template>
            </div>
        </div>
        <div class="ocr-form__content">
            <div class="ocr-form__content-container">
                <SplitPane
                    class="split-pane-container"
                    :initialLeft="previewInitialWidth"
                    :leftMin="minPreviewWidth"
                    :rightMin="minFormWidth"
                    :splitterWidth="splitterWidth"
                    storageKey="ocr-verification-split"
                >
                    <template #left>
                        <div v-if="loading" class="ocr-form__skeleton-container">
                            <CompassSkeleton
                                class="ocr-form__skeleton"
                                :variant="CompassEnums.SkeletonVariantType.BLOCK"
                            />
                        </div>
                        <div v-else class="ocr-form__preview">
                            <embed
                                v-if="preview.mime === 'application/pdf'"
                                :src="preview.url"
                                type="application/pdf"
                            />
                            <div v-else-if="preview.url" class="ocr-form__preview-image">
                                <img :src="preview.url" />
                            </div>
                        </div>
                    </template>

                    <!-- FORM -->
                    <template #right>
                        <OcrVerificationFormLoading v-if="loading" />
                        <div v-else class="ocr-form__sections-wrapper">
                            <CompassAlert
                                v-if="errorCode"
                                :type="CompassEnums.AlertType.ERROR"
                                :description="errorLabel"
                            />
                            <CompassLoader v-if="processing" />

                            <div v-show="!processing" class="ocr-form__sections">
                                <!-- Document -->
                                <CompassAccordion
                                    class="ocr-form__card-section ocr-form__content-document"
                                    :title="$t.__('ocr.OcrVerificationForm.document_section')"
                                    :type="CompassEnums.AccordionType.FUNCTIONAL"
                                    :coloredIconProps="{ icon: 'document', color: 'teal' }"
                                    open
                                >
                                    <div class="ocr-form__content--small">
                                        <div class="ocr-form__content-line-item">
                                            <CompassFormItem
                                                :label="$t.__('common.labels.type')"
                                                required
                                                :error="formErrors.type"
                                                data-error-field="type"
                                            >
                                                <CompassSelect
                                                    v-model="form.type"
                                                    :disabled="submitting"
                                                    :error="!!formErrors.type"
                                                    menuClass="ocr-form__select-fit-dropdown"
                                                >
                                                    <CompassDropdownOption
                                                        v-for="type in availableType"
                                                        :key="type.value"
                                                        :value="type.value"
                                                        :label="type.label"
                                                    />
                                                </CompassSelect>
                                            </CompassFormItem>
                                            <CompassFormItem
                                                :label="
                                                    $t.__('ocr.OcrVerificationForm.document_status')
                                                "
                                                required
                                                :error="formErrors.step"
                                                data-error-field="step"
                                            >
                                                <CompassSelect
                                                    v-model="form.step"
                                                    :disabled="submitting"
                                                    :type="CompassEnums.SelectType.TAG"
                                                    :error="!!formErrors.step"
                                                    menuClass="ocr-form__select-fit-dropdown"
                                                >
                                                    <CompassDropdownOption
                                                        v-for="step in availableStep"
                                                        :key="step.value"
                                                        :value="step.value"
                                                        :label="step.label"
                                                        :color="step.color"
                                                    >
                                                        <CompassTag
                                                            :color="step.color"
                                                            :size="CompassEnums.TagSize.SMALL"
                                                        >
                                                            {{ step.label }}
                                                        </CompassTag>
                                                    </CompassDropdownOption>
                                                </CompassSelect>
                                            </CompassFormItem>
                                        </div>
                                        <div class="ocr-form__content-line-item">
                                            <CompassFormItem
                                                :label="
                                                    $t.__('ocr.OcrVerificationForm.supplier_name')
                                                "
                                                required
                                                :error="formErrors.supplier"
                                                class="ocr-form__form-item--flex"
                                                data-error-field="supplier"
                                            >
                                                <CompassAutoComplete
                                                    :key="supplierSelectKey"
                                                    ref="supplierSelect"
                                                    :results="supplierResults"
                                                    :defaultValues="supplierDefaultValues"
                                                    :disabled="submitting"
                                                    :multiple="false"
                                                    :multipleLimit="1"
                                                    :triggerAfter="0"
                                                    menuClass="ocr-form__select-fit-dropdown"
                                                    :placeholder="
                                                        $t.__(
                                                            'ocr.OcrVerificationForm.enter_supplier_name',
                                                        )
                                                    "
                                                    :noResultsLabel="
                                                        $t.__(
                                                            'ocr.OcrVerificationForm.no_supplier_found',
                                                        )
                                                    "
                                                    :loading="supplierLoading"
                                                    :actions="supplierActions"
                                                    :error="!!formErrors.supplier"
                                                    @update:modelValue="onSupplierChange"
                                                    @update-search="onSupplierSearch"
                                                    @focus="onSupplierFocus"
                                                />
                                            </CompassFormItem>
                                            <CompassFormItem
                                                :label="
                                                    $t.__('ocr.OcrVerificationForm.document_date')
                                                "
                                                required
                                                :error="formErrors.documentDate"
                                                data-error-field="documentDate"
                                                class="ocr-form__form-item--date"
                                            >
                                                <CompassDatePicker
                                                    :modelValue="form.documentDate"
                                                    :disabled="submitting"
                                                    :formatDateDisplayed="formatDateDisplayed"
                                                    :error="!!formErrors.documentDate"
                                                    @update:modelValue="updateDate"
                                                />
                                            </CompassFormItem>
                                            <CompassFormItem
                                                :label="$t.__('common.labels.expected_close_date')"
                                                :required="!isCreditNote"
                                                :error="formErrors.dueDate"
                                                data-error-field="dueDate"
                                                class="ocr-form__form-item--date"
                                            >
                                                <CompassDatePicker
                                                    :modelValue="form.dueDate"
                                                    :disabled="submitting"
                                                    :formatDateDisplayed="formatDateDisplayed"
                                                    :error="!!formErrors.dueDate"
                                                    @update:modelValue="updateDueDate"
                                                />
                                            </CompassFormItem>
                                        </div>

                                        <div class="ocr-form__content-line-item">
                                            <CompassFormItem
                                                :label="$t.__('common.labels.currency')"
                                                required
                                                :error="formErrors.currency"
                                                class="ocr-form__form-item--currency"
                                                data-error-field="currency"
                                            >
                                                <CurrencyInput
                                                    :modelValue="form.currency"
                                                    :disabled="submitting"
                                                    mode="symbol"
                                                    :error="!!formErrors.currency"
                                                    @update:modelValue="updateCurrency"
                                                />
                                            </CompassFormItem>
                                            <CompassFormItem
                                                :label="
                                                    $t.__(
                                                        'ocr.OcrVerificationForm.external_supplier_document_number',
                                                    )
                                                "
                                                class="ocr-form__form-item--document-number"
                                                :error="formErrors.externalDocumentName"
                                                data-error-field="externalDocumentName"
                                            >
                                                <CompassInput
                                                    v-model="form.externalDocumentName"
                                                    :disabled="submitting"
                                                    :error="!!formErrors.externalDocumentName"
                                                />
                                            </CompassFormItem>
                                            <CompassFormItem
                                                v-if="analyticsMode === 'doc'"
                                                :label="
                                                    $t.__(
                                                        'document.docFormSettings.TabGeneralSettings.analytic_code_doc',
                                                    )
                                                "
                                                class="ocr-form__form-item--analytic-code"
                                            >
                                                <CompassInput
                                                    v-model="form.analyticCode"
                                                    :disabled="submitting"
                                                />
                                            </CompassFormItem>
                                        </div>

                                        <div class="ocr-form__content-line-item">
                                            <CompassFormItem
                                                :label="$t.__('common.labels.smart_tags')"
                                                class="ocr-form__form-item--full-width"
                                            >
                                                <CompassAutoComplete
                                                    :key="tagsSelectKey"
                                                    :results="tagsResults"
                                                    :defaultValues="tagDefaultValues"
                                                    :disabled="submitting"
                                                    multiple
                                                    :triggerAfter="1"
                                                    :placeholder="
                                                        $t.__('utilities.Tags.enter_smarttag')
                                                    "
                                                    :noResultsLabel="
                                                        $t.__('common.labels.no_result')
                                                    "
                                                    :loading="tagsLoading"
                                                    menuClass="ocr-form__select-fit-dropdown"
                                                    @update:modelValue="tagsUpdated"
                                                    @update-search="onTagsSearch"
                                                    @keydown.tab="onTagsTabPress"
                                                />
                                            </CompassFormItem>
                                        </div>

                                        <CompassButton
                                            v-if="!showNote"
                                            :label="$t.__('ocr.OcrVerificationForm.add_note')"
                                            :type="CompassEnums.ButtonType.TERTIARY"
                                            :disabled="submitting"
                                            leftIcon="plus"
                                            @click="showNote = true"
                                        />
                                        <CompassFormItem
                                            v-else
                                            :label="$t.__('common.labels.note')"
                                            class="ocr-form__form-item--full-width"
                                        >
                                            <CompassInput
                                                v-model="form.note"
                                                class="ocr-form__note"
                                                :disabled="submitting"
                                                :type="CompassEnums.InputType.TEXTAREA"
                                                :rows="6"
                                            />
                                        </CompassFormItem>
                                    </div>
                                </CompassAccordion>

                                <CompassAccordion
                                    class="ocr-form__card-section ocr-form__content-products"
                                    :title="$t.__('ocr.OcrVerificationForm.products_services')"
                                    :type="CompassEnums.AccordionType.FUNCTIONAL"
                                    :coloredIconProps="{ icon: 'modules', color: 'purple' }"
                                    open
                                    data-error-field="products"
                                >
                                    <OcrVerificationProductsTable
                                        ref="productsTable"
                                        class="ocr-form__products-table"
                                        :disabled="submitting"
                                        :products="form.products"
                                        :units="units"
                                        :rulesLine="rulesLine"
                                        :accountingCodes="accountingCodes"
                                        :taxRates="taxRates"
                                        :warehouses="warehouses"
                                        :isStockEnabled="isStockEnabled"
                                        :isLineItemsEnabled="isLineItemsEnabled"
                                        :showAnalyticCode="analyticsMode === 'item'"
                                        :currencySymbol="currencySymbol"
                                        :formatDateDisplayed="formatDateDisplayed"
                                        :hint="$t.__('ocr.OcrVerificationProductsTable.hint')"
                                        :addProduct="addProduct"
                                        :requestProductDelete="requestProductDelete"
                                        :updateDescription="updateDescription"
                                        :setProductDescriptionLabel="setProductDescriptionLabel"
                                        :hideDefaultValues="hideDefaultValues"
                                    />
                                    <div
                                        class="typographies-paragraph-medium-regular ocr-form__total"
                                    >
                                        <div class="ocr-form__total__amount-excluding-tax">
                                            <span>
                                                {{ $t.__('common.labels.amount_excluding_tax') }}
                                            </span>
                                            <span class="typographies-paragraph-medium-semibold">
                                                {{ formatAmount(getTotalWithoutTax) }}
                                            </span>
                                        </div>

                                        <template
                                            v-for="vatAmount in getTaxRows"
                                            :key="vatAmount.id"
                                        >
                                            <div class="ocr-form__vat-amount">
                                                <span>{{ `${vatAmount.rate}%` }}</span>
                                                <span>
                                                    {{
                                                        formatAmount(
                                                            getTotalByTax(
                                                                vatAmount.id,
                                                                vatAmount.rate,
                                                            ),
                                                        )
                                                    }}
                                                </span>
                                            </div>
                                        </template>
                                        <div class="ocr-form__vat-total">
                                            <span>
                                                {{ $t.__('ocr.OcrVerificationForm.total_tax') }}
                                            </span>
                                            <span class="typographies-paragraph-medium-semibold">
                                                {{ formatAmount(getTotalTax) }}
                                            </span>
                                        </div>
                                        <div class="ocr-form__total-incl-tax">
                                            <span>
                                                {{ $t.__('common.labels.amount_including_tax') }}
                                            </span>
                                            <span class="typographies-paragraph-medium-semibold">
                                                {{ formatAmount(getTotalWithTax) }}
                                            </span>
                                        </div>
                                    </div>
                                </CompassAccordion>

                                <CompassAccordion
                                    v-if="canAddPayment"
                                    class="ocr-form__card-section ocr-form__content-payment"
                                    :title="$t.__(
                                        isCreditNote
                                            ? 'ocr.OcrVerificationForm.refund_title'
                                            : 'ocr.OcrVerificationForm.payment_title'
                                    )"
                                    :type="CompassEnums.AccordionType.FUNCTIONAL"
                                    :coloredIconProps="{ icon: 'pig-money', color: 'orange' }"
                                    open
                                    data-error-field="payment"
                                >
                                    <div class="ocr-form__content--small">
                                        <div v-for="(payment, index) of form.payments" :key="index">
                                            <div class="ocr-form__payment-header">
                                                <h3 class="typographies-heading-h3">
                                                    {{
                                                        $t.__(
                                                            isCreditNote
                                                                ? 'ocr.OcrVerificationForm.refund_item_title'
                                                                : 'ocr.OcrVerificationForm.payment_item_title',
                                                            { number: index + 1 },
                                                        )
                                                    }}
                                                </h3>
                                                <CompassIconButton
                                                    v-if="hasMultiplePayments"
                                                    icon="bin"
                                                    :disabled="submitting"
                                                    :variant="
                                                        CompassEnums.IconButtonVariant.DESTRUCTIVE
                                                    "
                                                    :size="CompassEnums.IconButtonSize.MEDIUM"
                                                    @click="deletePayment(index)"
                                                />
                                            </div>
                                            <div class="ocr-form__content-line-item">
                                                <CompassFormItem
                                                    :label="
                                                        $t.__(
                                                            isCreditNote
                                                                ? 'ocr.OcrVerificationForm.refund_date'
                                                                : 'ocr.OcrVerificationForm.payment_date',
                                                        )
                                                    "
                                                    :required="isPaymentFieldRequired(payment)"
                                                    :error="getPaymentError(index, 'date')"
                                                    class="ocr-form__form-item--date"
                                                >
                                                    <CompassDatePicker
                                                        v-model="payment.date"
                                                        :disabled="submitting"
                                                        :formatDateDisplayed="formatDateDisplayed"
                                                        :error="!!getPaymentError(index, 'date')"
                                                    />
                                                </CompassFormItem>
                                                <CompassFormItem
                                                    :label="
                                                        $t.__(
                                                            isCreditNote
                                                                ? 'ocr.OcrVerificationForm.refund_reference'
                                                                : 'ocr.OcrVerificationForm.payment_reference',
                                                        )
                                                    "
                                                    class="ocr-form__form-item--payment-reference"
                                                >
                                                    <CompassInput
                                                        v-model="payment.reference"
                                                        :disabled="submitting"
                                                    />
                                                </CompassFormItem>
                                                <CompassFormItem
                                                    :label="$t.__('common.labels.amount')"
                                                    :required="isPaymentFieldRequired(payment)"
                                                    :error="getPaymentError(index, 'amount')"
                                                    class="ocr-form__form-item--payment-amount"
                                                >
                                                    <CompassInput
                                                        :modelValue="payment.amount"
                                                        :disabled="submitting"
                                                        inputmode="decimal"
                                                        :error="!!getPaymentError(index, 'amount')"
                                                        @update:modelValue="
                                                            payment.amount =
                                                                limitToTwoDecimals($event)
                                                        "
                                                        @blur="
                                                            formatFieldToTwoDecimals(
                                                                payment,
                                                                'amount',
                                                            )
                                                        "
                                                    >
                                                        <template v-if="currencySymbol" #suffix>
                                                            {{ currencySymbol }}
                                                        </template>
                                                    </CompassInput>
                                                </CompassFormItem>
                                                <CompassFormItem
                                                    :label="
                                                        $t.__(
                                                            isCreditNote
                                                                ? 'common.labels.refund_method'
                                                                : 'common.labels.payment_method',
                                                        )
                                                    "
                                                    :required="isPaymentFieldRequired(payment)"
                                                    :error="getPaymentError(index, 'payment_mean')"
                                                    class="ocr-form__form-item--payment-method"
                                                >
                                                    <CompassSelect
                                                        v-model="payment.payment_mean"
                                                        :disabled="submitting"
                                                        filterable
                                                        :error="
                                                            !!getPaymentError(index, 'payment_mean')
                                                        "
                                                        :placeholder="
                                                            $t.__(
                                                                isCreditNote
                                                                    ? 'ocr.OcrVerificationForm.select_refund_method'
                                                                    : 'ocr.OcrVerificationForm.select_payment_method',
                                                            )
                                                        "
                                                    >
                                                        <CompassDropdownOption
                                                            :value="undefined"
                                                            :label="
                                                                $t.__('common.labels.none_option')
                                                            "
                                                        />
                                                        <CompassDropdownOption
                                                            v-for="item in paymentTypes"
                                                            :key="item.id"
                                                            :label="item.label"
                                                            :value="item.id"
                                                        />
                                                    </CompassSelect>
                                                </CompassFormItem>
                                            </div>

                                            <CompassButton
                                                v-if="index === form.payments.length - 1"
                                                :label="
                                                    $t.__(
                                                        isCreditNote
                                                            ? 'ocr.OcrVerificationForm.add_refund'
                                                            : 'ocr.OcrVerificationForm.add_payment',
                                                    )
                                                "
                                                :type="CompassEnums.ButtonType.TERTIARY"
                                                :disabled="submitting"
                                                leftIcon="plus"
                                                @click="addPayment"
                                            />
                                        </div>
                                    </div>
                                </CompassAccordion>
                            </div>
                        </div>
                    </template>
                </SplitPane>
            </div>
        </div>

        <div class="ocr-form__footer">
            <div class="ocr-form__footer-actions">
                <CompassButton
                    :label="$t.__('common.actions.delete')"
                    :type="CompassEnums.ButtonType.TERTIARY_DESTRUCTIVE"
                    :disabled="submitting"
                    data-bot="purchases-purinvoices-verify_delete"
                    @click="deleteOCR"
                />
                <CompassButton
                    :label="$t.__('common.actions.ignore')"
                    :type="CompassEnums.ButtonType.SECONDARY"
                    :disabled="submitting"
                    data-bot="purchases-purinvoices-verify_ignore"
                    @click="skip"
                />
                <CompassButton
                    nativeType="button"
                    :label="$t.__('common.actions.validate')"
                    :type="CompassEnums.ButtonType.PRIMARY"
                    :loading="submitting"
                    data-bot="purchases-purinvoices-verify_validate"
                    @click="submit"
                />
            </div>
        </div>
        <DeleteOcrProductLineModal
            :visible="productDeleteModalVisible"
            :rememberChoice="productDeleteRememberChoice"
            @update:visible="productDeleteModalVisible = $event"
            @update:rememberChoice="productDeleteRememberChoice = $event"
            @confirm="onProductDeleteConfirm"
            @cancel="onProductDeleteCancel"
        />
    </div>
</template>
<script>
import Heading from 'vcomponents/common/Heading/Heading.vue';
import CurrencyInput from 'vcomponents/inputs/CurrencyInput.vue';

import OcrVerificationProductsTable from 'vcomponents/ocr/OcrVerificationProductsTable.vue';
import OcrVerificationFormLoading from './OcrVerificationFormLoading.vue';
import DeleteOcrProductLineModal from './DeleteOcrProductLineModal.vue';
import LocalStorage from 'Core/commons/LocalStorage';
import { goToUrl } from '../../helpers';
import { hydrateOcr } from 'Services/Ocr/Ocr.hydrator';
import { persistOcr } from 'Services/Ocr/Ocr.persistor';
import {
    calculateTotalWithoutTax,
    calculateTotalWithTax,
    calculateTotalTax,
    getUniqueTaxRows,
    calculateTotalByTax,
    createEmptyProduct,
    formatFieldToTwoDecimals,
    limitToTwoDecimals,
    setProductDescriptionFromAccountingCode,
    AMOUNT_DECIMAL_PLACES,
} from 'composables/useOcrProducts';
import {
    createFormRules,
    validateFormWithRules,
    createProductLineRules,
    isPaymentFieldRequired,
} from 'composables/useOcrValidation';
import { createNamespacedHelpers } from 'vuex';
import Ocr from 'api/ocr';
import Payments from 'api/payments';
import Stock from 'api/stock';
import Feature from 'Services/Feature';
import Accounting from 'api/accounting';
import Companies from 'api/companies';
import OcrVerification from 'stores/ocrVerification';
import SplitPane from 'vcomponents/layout/SplitPane.vue';

const corpMapState = createNamespacedHelpers('corp').mapState;
const { mapState } = createNamespacedHelpers('system');

export default {
    components: {
        Heading,
        CurrencyInput,
        OcrVerificationProductsTable,
        OcrVerificationFormLoading,
        DeleteOcrProductLineModal,
        SplitPane,
    },
    props: {
        canCreateSupplier: {
            type: Boolean,
            required: true,
        },
        linkedtype: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            processing: false,
            submitting: false,
            keepDefaultValues: true,
            showNote: false,
            availableType: [
                {
                    label: this.$t.__('common.labels.invoice'),
                    value: 'purInvoice',
                },
                {
                    label: this.$t.__('common.labels.creditnote'),
                    value: 'purCreditNote',
                },
            ],
            presetSupplier: [],
            paymentTypes: [],
            accountingCodes: [],
            defaultAccountingCodes: null,
            defaultTags: [],
            tagsResults: [],
            tagsLoading: false,
            tagsSearchQuery: '',
            tagsSelectKey: 0,
            taxRates: [],
            warehouses: [],
            preview: {
                mime: null,
                url: null,
            },
            previewInitialWidth: 640,
            minPreviewWidth: 360,
            minFormWidth: 520,
            splitterWidth: 12,
            remoteSupplier: '',
            provider: '',
            supplierSelectKey: 0,
            supplierResults: [],
            supplierLoading: false,
            supplierActions: [],
            analyticsMode: null,
            form: {
                type: this.linkedtype || 'purInvoice',
                supplier: null,
                documentDate: '',
                dueDate: '',
                externalDocumentName: '',
                analyticCode: '',
                currency: null,
                step: this.linkedtype === 'purCreditNote' ? 'stored' : 'due',
                note: '',
                products: [createEmptyProduct()],
                payments: [
                    {
                        date: '',
                        reference: '',
                        amount: null,
                        payment_mean: '',
                    },
                ],
                tags: [],
                totalWithoutTax: null,
                totalWithTax: null,
            },
            ocrLocalStorage: new LocalStorage('ocr', 'form.verification'),
            ocrRows: [],
            multipleVerify: false,
            totalRows: 0,
            currencySymbol: null,
            errorCode: null,
            defaultAccountingCodeCompany: null,
            ocrId: null,
            productDeleteModalVisible: false,
            productDeleteIndex: null,
            productDeleteRememberChoice: false,
            formErrors: {
                type: '',
                step: '',
                supplier: '',
                documentDate: '',
                dueDate: '',
                currency: '',
            },
            paymentErrors: [],
        };
    },
    computed: {
        ...corpMapState({
            formatDate: 'timeformat',
            units: 'units',
            uiLang: 'uiLang',
        }),
        ...mapState({
            allCurrencies: 'currencies',
            errorsHandler: 'errorsHandler',
        }),
        formatDateDisplayed() {
            return this.formatDate === 'dmy' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
        },
        isCreditNote() {
            return this.form.type === 'purCreditNote';
        },
        rules() {
            const rules = createFormRules(this.$t);
            if (this.isCreditNote) {
                delete rules.dueDate;
            }
            return rules;
        },
        availableType() {
            return [
                {
                    label: this.$t.__('ocr.OcrVerificationForm.type_invoice'),
                    value: 'purInvoice',
                },
                {
                    label: this.$t.__('ocr.OcrVerificationForm.type_credit_note'),
                    value: 'purCreditNote',
                    enabled: this.canCreateCreditNote,
                },
            ].filter((type) => type.enabled !== false);
        },
        availableStep() {
            if (this.isCreditNote) {
                return [
                    {
                        label: this.$t.__('ocr.OcrVerificationForm.step_draft'),
                        value: 'draft',
                        color: 'purple',
                    },
                    {
                        label: this.$t.__('ocr.OcrVerificationForm.step_stored'),
                        value: 'stored',
                        color: 'yellow',
                    },
                ];
            }
            return [
                {
                    label: this.$t.__('ocr.OcrVerificationForm.step_draft'),
                    value: 'draft',
                    color: 'purple',
                },
                {
                    label: this.$t.__('ocr.OcrVerificationForm.step_due'),
                    value: 'due',
                    color: 'yellow',
                },
            ];
        },
        isStockEnabled() {
            return Feature.isEnabled('ocr-stock');
        },
        isLineItemsEnabled() {
            return Feature.isEnabled('ocr-line-items') && Feature.isEnabled('ocr-mistral');
        },
        canCreateCreditNote() {
            return this.permissions['create_pur_credit_note'] !== false;
        },
        canAddPayment() {
            return this.permissions['create_payment'];
        },
        hasMultiplePayments() {
            return this.form.payments.length > 1;
        },
        permissions() {
            return this.$store.getters['ocr/permissions'];
        },
        rulesLine() {
            return createProductLineRules(this.$t);
        },
        urlBackToList() {
            return '/ocr/list/purInvoice';
        },
        getAutocompleteRoute() {
            return this.$router.resolve({ name: 'legacy.third.autocomplete' }).href;
        },
        presetSupplierOptions() {
            return this.presetSupplier.map((supplier) => ({
                value: supplier.id,
                label: supplier.name,
            }));
        },
        supplierDefaultValues() {
            const preset = this.presetSupplier.find(
                (supplier) => supplier.id === this.form.supplier,
            );
            return preset ? [{ value: preset.id, label: preset.name }] : [];
        },
        tagDefaultValues() {
            return (this.form.tags || []).map((tag) => {
                const id = tag.id ?? tag.value;
                const label = String(tag.value ?? '');
                return { id, value: id, label };
            });
        },
        defaultTaxRateId() {
            return this.taxRates.length > 0 ? this.taxRates[0].id : null;
        },
        getTotalWithoutTax() {
            return calculateTotalWithoutTax(
                this.form.products,
                this.keepDefaultValues,
                this.form.totalWithoutTax,
            );
        },
        getTotalTax() {
            return calculateTotalTax(this.form.products, this.getTaxRows);
        },
        getTotalWithTax() {
            if (this.keepDefaultValues) {
                return this.form.totalWithTax;
            }
            return calculateTotalWithTax(this.getTotalWithoutTax, this.getTotalTax);
        },
        getTaxRows() {
            return getUniqueTaxRows(this.form.products, this.taxRates);
        },
        completeRows() {
            return this.totalRows - this.ocrRows.length;
        },
        isElectronic() {
            return this.provider === 'facturx';
        },
        documentTypeLabel() {
            if (this.isElectronic) {
                return this.isCreditNote
                    ? this.$t.__('ocr.OcrVerificationForm.type_electronic_credit_note')
                    : this.$t.__('ocr.OcrVerificationForm.type_electronic_invoice');
            }
            return this.isCreditNote
                ? this.$t.__('ocr.OcrVerificationForm.type_imported_credit_note')
                : this.$t.__('ocr.OcrVerificationForm.type_imported_invoice');
        },
        third() {
            return window.Third;
        },
        errorLabel() {
            const errorMessage =
                this.errorCode === 1
                    ? this.$t.__('ocr.OcrVerificationForm.error_too_many_pages')
                    : this.$t.__('ocr.OcrVerificationForm.error_technical');
            const manualEntryMessage = this.$t.__('ocr.OcrVerificationForm.error_manual_entry');
            return `${errorMessage} ${manualEntryMessage}`;
        },
    },
    watch: {
        'form.type'() {
            const validSteps = this.availableStep.map((step) => step.value);
            if (!validSteps.includes(this.form.step)) {
                this.form.step = validSteps[1] ?? validSteps[0];
            }
        },
    },
    created() {
        const localContent = this.ocrLocalStorage.get() || {};
        this.ocrRows = localContent.rows || [];
        this.multipleVerify = localContent.multipleVerify || false;
        this.totalRows = localContent.totalRows || 0;

        const regex = new RegExp(/\/purchase\/incoming\/verification\/(?<id>\d+)$/);
        this.ocrId = regex.exec(this.$route.path)?.groups?.id;
        this.getDictionnaries();
        if (!this.$store.state.ocr) {
            this.$store.registerModule('ocr', OcrVerification);
        }
        this.$store.dispatch('ocr/init');

        EventBus.on('third:created', (params) => {
            this.getSupplier(params.thirdid);
        });
    },
    unmounted() {
        EventBus.off('third:created');
    },
    methods: {
        isPaymentFieldRequired,
        formatFieldToTwoDecimals,
        limitToTwoDecimals,
        resetFormErrors() {
            this.formErrors = {
                type: '',
                step: '',
                supplier: '',
                documentDate: '',
                dueDate: '',
                currency: '',
                externalDocumentName: '',
            };
            this.paymentErrors = this.form.payments.map(() => ({
                date: '',
                amount: '',
                payment_mean: '',
            }));
        },
        validateFormFields() {
            this.resetFormErrors();
            const requiredMessage = this.$t.__('common.labels.this_field_is_required');

            const { errors, firstErrorField: ruleErrorField } = validateFormWithRules(
                this.form,
                this.rules,
            );
            Object.assign(this.formErrors, errors);
            let firstErrorField = ruleErrorField;

            for (let index = 0; index < this.form.payments.length; index++) {
                const payment = this.form.payments[index];
                if (!this.isPaymentFieldRequired(payment)) {
                    continue;
                }

                if (_.isEmpty(payment.date)) {
                    this.paymentErrors[index].date = requiredMessage;
                    firstErrorField ??= 'payment';
                }
                if (_.isNil(payment.amount) || payment.amount === '') {
                    this.paymentErrors[index].amount = requiredMessage;
                    firstErrorField ??= 'payment';
                }
                if (!_.isNumber(payment.payment_mean)) {
                    this.paymentErrors[index].payment_mean = requiredMessage;
                    firstErrorField ??= 'payment';
                }
            }

            return { isValid: !firstErrorField, firstErrorField };
        },
        getPaymentError(index, field) {
            return this.paymentErrors[index]?.[field] || '';
        },
        scrollToFirstError(fieldName) {
            this.$nextTick(() => {
                const element = this.$el.querySelector(`[data-error-field="${fieldName}"]`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            });
        },
        addProduct(product) {
            const newProduct = !!product
                ? _.cloneDeep(product)
                : createEmptyProduct({
                    defaultAccountingCode: this.defaultAccountingCode,
                    defaultTaxRateId: this.defaultTaxRateId,
                    supplierId: this.form.supplier,
                    companyAccountingCode: this.defaultAccountingCodeCompany,
                });
            this.form.products.push(newProduct);
            this.keepDefaultValues = false;
        },
        addPayment() {
            this.form.payments.push({
                date: '',
                reference: '',
                amount: null,
                payment_mean: '',
            });
        },
        deletePayment(index) {
            this.form.payments.splice(index, 1);
        },
        requestProductDelete(index) {
            if (this.productDeleteRememberChoice) {
                this.form.products.splice(index, 1);
                return;
            }
            this.productDeleteIndex = index;
            this.productDeleteModalVisible = true;
        },
        closeProductDeleteModal() {
            this.productDeleteModalVisible = false;
            this.productDeleteIndex = null;
        },
        onProductDeleteConfirm() {
            if (this.productDeleteIndex !== null) {
                this.form.products.splice(this.productDeleteIndex, 1);
            }

            this.closeProductDeleteModal();
        },
        onProductDeleteCancel() {
            this.closeProductDeleteModal();
        },
        getSupplier(id) {
            this.processing = true;
            Companies.getCompanyById(id, { field: ['id', 'name'] }).then((response) => {
                this.processing = false;
                this.form.supplier = response.data.id;
                this.presetSupplier.push(response.data);
                this.supplierSelectKey++;
            });
        },
        getOcrById() {
            Ocr.getOcrById(this.ocrId)
                .then((response) => {
                    if (response.state === 'error') {
                        this.errorCode = response.error_code;
                    } else {
                        this.provider = response.provider || '';
                        const { formData, analyticsMode } = hydrateOcr(response.response, {
                            taxList: this.taxRates,
                            accountingCodes: this.accountingCodes,
                            defaultAccountingCode: this.defaultAccountingCode,
                        });
                        this.analyticsMode = analyticsMode;
                        this.form = {
                            ...this.form,
                            ...formData,
                        };
                        if (response.linked_type) {
                            this.form.type = response.linked_type;
                        }
                        this.currencySymbol = this.allCurrencies.find(
                            (currency) => this.form.currency === currency.name,
                        )?.symbol;
                        const supplierData = response.response.supplier;
                        if (supplierData?.id !== null && supplierData?.value !== null) {
                            const alreadyExists = this.presetSupplier.some(
                                (supplier) => supplier.id === supplierData.id,
                            );
                            if (!alreadyExists) {
                                this.presetSupplier.push({
                                    id: parseInt(supplierData.id),
                                    name: supplierData.value,
                                });
                            }
                        } else if (supplierData?.value !== null) {
                            this.remoteSupplier = supplierData.value;
                        }

                        if (!_.isNil(this.form.supplier)) {
                            this.updateAccountingCodeProduct(this.form.supplier);
                        }
                    }

                    if (!_.isNil(response.preview)) {
                        this.preview = response.preview;
                    }

                    this.loading = false;
                })
                .catch((error) => {
                    this.$router.push({
                        name: 'error.not.found',
                        params: {
                            title: this.$t.__('ocr.OcrVerificationForm.document_not_found'),
                            buttonOptions: {
                                label: this.$t.__('ocr.OcrVerificationForm.back_to_list'),
                                url: this.urlBackToList,
                            },
                        },
                    });
                    this.loading = false;
                    this.errorsHandler.logException(error);
                });
        },
        getDictionnaries() {
            this.loading = true;
            Promise.all([
                Accounting.getAllAccountingCodes(
                    { limit: 100, order: 'code' },
                    { filters: { views: ['purchase'] } },
                ),
                Accounting.getTaxes({}, { filters: { is_active: true } }),
                Accounting.getDefaultAccountingPurchaseCode(),
                Companies.getCompanies(
                    {
                        limit: 10,
                        order: 'created_at',
                        direction: 'desc',
                        field: ['id', 'name'],
                    },
                    { type: 'supplier' },
                ),
                Payments.getAllPayments(),
                Ocr.getTagsPurInvoice(),
                Feature.isEnabled('ocr-stock')
                    ? Stock.getWarehouses().catch(() => [])
                    : Promise.resolve([]),
            ]).then((response) => {
                this.accountingCodes = response[0];
                this.taxRates = response[1].data.data.map((tax) => {
                    tax.rate = parseFloat(tax.rate).toFixed(2);
                    return tax;
                });
                this.defaultAccountingCode = +response[2];
                this.presetSupplier = response[3].data.data;
                this.paymentTypes = response[4];
                this.getOcrById();
                this.defaultTags = response[5];
                this.tagsResults = this.normalizeTagOptions(this.defaultTags);
                this.warehouses = response[6] ?? [];
            });
        },
        /**
         * @param {number} value - Amount to format
         * @returns {string} Formatted currency string with symbol placed according to locale with currency symbol if available, decimal otherwise
         */
        formatAmount(value) {
            const options = {
                minimumFractionDigits: AMOUNT_DECIMAL_PLACES,
                maximumFractionDigits: AMOUNT_DECIMAL_PLACES,
                ...(this.form.currency
                    ? { style: 'currency', currency: this.form.currency }
                    : { style: 'decimal' }),
            };
            return new Intl.NumberFormat(this.uiLang, options).format(value ?? 0);
        },
        getTotalByTax(taxId, taxRate) {
            return calculateTotalByTax(this.form.products, taxId, taxRate);
        },
        cancel() {
            this.ocrLocalStorage.delete();
            goToUrl(this.urlBackToList);
        },
        async submit() {
            if (this.submitting) {
                return;
            }
            this.submitting = true;

            const { isValid: formFieldsValid, firstErrorField } = this.validateFormFields();
            const productsValid = await this.$refs.productsTable?.validate?.();

            if (!formFieldsValid || productsValid === false) {
                this.submitting = false;
                if (firstErrorField) {
                    this.scrollToFirstError(firstErrorField);
                } else if (productsValid === false) {
                    this.scrollToFirstError('products');
                }
                return;
            }

            const formData = persistOcr(
                {
                    ...this.form,
                    totalWithoutTax: this.getTotalWithoutTax,
                    totalWithTax: this.getTotalWithTax,
                },
                { currencies: this.allCurrencies, taxRates: this.taxRates },
            );
            Ocr.submitVerification(this.ocrId, this.form.type, formData)
                .then((response) => {
                    const rowsRemains = this.ocrRows.filter(
                        (row) => row.id.toString() !== this.ocrId,
                    );
                    if (rowsRemains.length && this.multipleVerify) {
                        this.goNextOcr(rowsRemains, 0);
                    } else {
                        const isMultiple = this.multipleVerify;
                        this.ocrLocalStorage.delete();

                        const isCreditNote = this.form.type === 'purCreditNote';
                        const messageKey = isCreditNote
                            ? isMultiple
                                ? 'ocr.OcrVerificationForm.success_credit_note_multiple'
                                : 'ocr.OcrVerificationForm.success_credit_note_single'
                            : isMultiple
                                ? 'ocr.OcrVerificationForm.success_invoice_multiple'
                                : 'ocr.OcrVerificationForm.success_invoice_single';
                        const message = this.$t.__(messageKey);

                        this.$pushToast({
                            type: this.CompassEnums.ToastType.SUCCESS,
                            icon: true,
                            text: message,
                            timeout: 5000,
                        });

                        if (isMultiple) {
                            const listRoute = isCreditNote
                                ? 'purchasecreditnotes'
                                : 'purchaseinvoices';
                            this.$router.push({ name: listRoute });
                        } else {
                            const overviewRoute = isCreditNote
                                ? 'purchases_purCreditNoteOverview'
                                : 'purchases_purInvoiceOverview';
                            goToUrl(`/?_f=${overviewRoute}&id=${response.docid}`);
                        }
                    }
                })
                .catch((err) => {
                    this.errorsHandler.logException(err);
                    this.$pushToast({
                        type: this.CompassEnums.ToastType.ERROR,
                        icon: true,
                        text: this.$t.__('ocr.OcrVerificationForm.error_generic'),
                        timeout: 5000,
                    });
                    this.submitting = false;
                });
        },
        goNextOcr(rowsRemains, nextIndex) {
            this.ocrRows = rowsRemains;
            this.ocrLocalStorage.set({
                rows: rowsRemains,
                multipleVerify: this.multipleVerify,
                totalRows: this.totalRows,
            });
            goToUrl(`/purchase/incoming/verification/${rowsRemains[nextIndex].id}`);
        },
        deleteOCR() {
            this.processing = true;
            Ocr.deleteOcr(this.ocrId).then(() => {
                this.processing = false;
                const rowsRemains = this.ocrRows.filter((row) => row.id.toString() !== this.ocrId);
                if (rowsRemains.length && this.multipleVerify) {
                    this.goNextOcr(rowsRemains, 0);
                } else {
                    this.ocrLocalStorage.delete();
                    goToUrl(this.urlBackToList);
                }
            });
        },
        addSupplier() {
            this.third.create('supplier', 'noRedirect', null, null, null, {
                corp_name: this.remoteSupplier,
            });
        },
        skip() {
            this.processing = true;
            if (this.multipleVerify) {
                const currentIndex = this.ocrRows.findIndex(
                    (row) => row.id.toString() === this.ocrId,
                );
                const nextIndex = _.isNil(this.ocrRows[currentIndex + 1]) ? 0 : currentIndex + 1;
                this.goNextOcr(this.ocrRows, nextIndex);
            } else {
                this.ocrLocalStorage.delete();
                goToUrl(this.urlBackToList);
            }
        },
        updateDescription(description) {
            this.form.description = description;
        },
        updateCurrency(currency) {
            this.form.currency = currency.name;
            this.currencySymbol = currency.symbol;
        },
        updateSupplier(supplier) {
            this.updateAccountingCodeProduct(supplier);
            this.form.supplier = supplier;
        },
        onSupplierChange(selected) {
            if (!selected) {
                this.form.supplier = null;
                this.supplierSelectKey++;
                return;
            }
            const supplierId = selected.value;
            this.updateSupplier(supplierId);
        },
        updateSupplierActions(query) {
            this.supplierActions = this.canCreateSupplier
                ? [
                    {
                        label: this.$t
                            .__('ocr.OcrVerificationForm.create_supplier', {
                                supplier: query || '',
                            })
                            .trim(),
                        callback: this.addSupplier,
                    },
                ]
                : [];
        },
        onSupplierFocus(query) {
            this.onSupplierSearch(query ?? '');
        },
        onSupplierSearch(query) {
            this.remoteSupplier = query;

            if (!query) {
                this.supplierResults = [...this.presetSupplierOptions];
                this.updateSupplierActions('');
                this.supplierLoading = false;
                return;
            }

            this.supplierLoading = true;

            this.$ajax.get(
                this.getAutocompleteRoute,
                { q: query, types: ['supplier'] },
                (response) => {
                    this.supplierResults = response.map((supplier) => ({
                        value: supplier.id,
                        label: supplier.name,
                    }));
                    this.updateSupplierActions(query);
                    this.supplierLoading = false;
                },
                () => {
                    this.supplierLoading = false;
                },
            );
        },
        onTagsSearch(query) {
            this.tagsSearchQuery = (query || '').toString();
            const normalizedQuery = this.tagsSearchQuery.trim().toLowerCase();
            const tagsToUse = normalizedQuery
                ? this.defaultTags.filter((tag) =>
                    (tag.value || '').toString().toLowerCase().includes(normalizedQuery),
                )
                : this.defaultTags;
            this.tagsResults = this.normalizeTagOptions(tagsToUse);
        },
        updateDate(date) {
            this.form.documentDate = date;
        },
        updateDueDate(date) {
            this.form.dueDate = date;
        },
        hideDefaultValues() {
            this.keepDefaultValues = false;
        },
        setProductDescriptionLabel(product) {
            setProductDescriptionFromAccountingCode(product, this.accountingCodes);
        },
        tagsUpdated(tags) {
            this.form.tags = (tags || []).map((tag) => {
                const rawValue = tag.label ?? tag.value;
                const id =
                    tag.id ??
                    (typeof tag.value === 'number' || typeof tag.value === 'string'
                        ? tag.value
                        : null);
                return { id, value: String(rawValue ?? '') };
            });
        },
        normalizeTagOptions(tags) {
            return (tags || []).map((tag) => {
                const id = tag.id ?? tag.value;
                const label = String(tag.value ?? tag.label ?? '');
                return { id, value: id, label };
            });
        },
        onTagsTabPress() {
            const query = this.tagsSearchQuery.trim();
            if (!query) {
                return;
            }
            const alreadyExists = this.defaultTags.some(
                (tag) => (tag.value || '').toString().toLowerCase() === query.toLowerCase(),
            );
            if (alreadyExists) {
                return;
            }
            this.form.tags.push({ id: null, value: query });
            this.defaultTags.push({ id: query, value: query });
            this.tagsSearchQuery = '';
            this.tagsSelectKey++;
        },
        updateAccountingCodeProduct(supplier) {
            Companies.getCompanyById(supplier, { field: ['accounting_purchase_code_id'] }).then(
                (response) => {
                    this.defaultAccountingCodeCompany =
                        response.data.accounting_purchase_code_id === null
                            ? this.defaultAccountingCode
                            : response.data.accounting_purchase_code_id;
                    for (const product of this.form.products) {
                        if (!product.catalogueLink) {
                            product.accountingCode = this.defaultAccountingCodeCompany;
                            this.setProductDescriptionLabel(product);
                        }
                    }
                },
            );
        },
    },
};
</script>

<style lang="scss">
.page-purchase-incoming-verification {
    #sellsy_app {
        background-color: var(--colors-common-background-app);
    }
    #contentDiv {
        overflow: hidden;
    }
    .contentMargin {
        margin: calc(-1 * var(--spacing-5)) calc(-1 * var(--spacing-4));
    }
}

#compass-dropdown-container {
    .ocr-form__select-fit-dropdown {
        width: auto;
    }
}
</style>

<style lang="scss" scoped>
.ocr-form {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 60px);

    &__form-item--flex {
        flex: 1;
    }

    &__form-item--date {
        width: 180px;
    }

    &__form-item--currency {
        max-width: 150px;
    }

    &__form-item--document-number {
        flex: 1;
        min-width: 265px;
    }

    &__form-item--analytic-code {
        width: 200px;
    }

    &__form-item--full-width {
        width: 100%;
    }

    &__form-item--payment-reference {
        flex: 1;
        min-width: 160px;
    }

    &__form-item--payment-amount {
        max-width: 130px;
    }

    &__form-item--payment-method {
        max-width: 190px;
    }

    &__header {
        background-color: var(--colors-common-background-default);
        border-bottom: solid 1px var(--colors-common-border-default);
        padding: var(--spacing-4);
        display: flex;
        align-items: center;
        flex-direction: row;
        gap: var(--spacing-1);

        :deep(.heading) {
            flex: 1;
            &.heading--h1 {
                margin: 0;
            }
        }

        &__info {
            gap: var(--spacing-1);
        }
    }

    &__header-remaining {
        line-height: var(--line-heights-24);
        font-size: var(--font-size-14);
        color: var(--colors-common-text-standard-caption);
    }

    &__content {
        flex: 1;
        overflow: hidden;
        display: flex;
        flex-direction: row;
        align-items: stretch;
        width: 100%;

        &--small {
            max-width: 720px;
        }
    }

    h3 {
        margin: 0;
    }

    &__products-table {
        border-bottom: 1px solid var(--colors-common-border-lighter);
        padding-bottom: var(--spacing-1);
        margin-bottom: var(--spacing-3);
    }

    &__vat-amount {
        display: flex;
        width: 100%;
        justify-content: space-between;
        color: var(--colors-common-text-subtle-default);
    }

    &__vat-total {
        display: flex;
        width: 100%;
        justify-content: space-between;
        margin: var(--spacing-1) 0;
    }

    &__total {
        width: 312px;
        display: flex;
        flex-direction: column;
        margin-left: auto;

        &__amount-excluding-tax {
            display: flex;
            width: 100%;
            justify-content: space-between;
            padding-bottom: var(--spacing-1);
            border-bottom: 1px solid var(--colors-common-border-lighter);
            margin-bottom: var(--spacing-1);
        }

        &-incl-tax {
            display: flex;
            width: 100%;
            justify-content: space-between;
            background-color: var(--colors-common-background-subtle);
            padding: var(--spacing-1);
            border-radius: var(--spacing-0);
        }
    }

    &__content-container {
        display: flex;
        flex-direction: row;
        width: 100%;
        height: 100%;

        :deep(.split-pane-container) {
            display: flex;
            flex-direction: row;
            justify-content: center;
            width: 100%;
            height: 100%;
            align-items: stretch;
        }

        :deep(.split-pane-container .split-pane__pane) {
            height: 100%;
            box-sizing: border-box;
        }

        :deep(.split-pane-container .split-pane__pane--right) {
            padding: var(--spacing-4);
            overflow-y: auto;
            overflow-x: hidden;
        }
    }

    &__sections {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-4);
    }

    &__card-section {
        background: var(--colors-common-background-default);
        border-radius: var(--spacing-1);
        box-shadow: var(--elevation-1);
        padding: var(--spacing-4);
    }

    &__content-line-item {
        display: flex;
        flex-direction: row;
        align-items: end;
        gap: var(--spacing-3);
        margin-bottom: var(--spacing-4);
        flex-wrap: wrap;

        :deep(.compass-form-item__input) {
            > div {
                width: 100%;
            }
        }
    }

    &__note {
        width: 100%;

        :deep(.compass-input__inner) {
            width: 100%;
        }
    }

    &__payment-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--spacing-2);
    }

    &__skeleton {
        width: 100%;
        height: 100%;
        :deep(.compass-skeleton__block) {
            height: 100%;
        }
        &-container {
            height: 100%;
            background-color: var(--colors-common-background-default);
            padding: var(--spacing-4);
        }
    }

    &__preview {
        > embed {
            width: 100%;
            height: 100%;
        }
        &-image {
            width: 100%;
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            overflow: hidden;
            background-color: #282828;

            img {
                width: 100%;
                height: 100%;
                object-fit: contain;
                object-position: center center;
            }
        }
    }

    &__footer {
        background-color: var(--colors-common-background-default);
        padding: var(--spacing-3) var(--spacing-4);
        box-shadow: var(--box-shadows-medium);
        z-index: 1;
        &-actions {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: var(--spacing-2);
        }
    }
}
</style>
